import { ReactNode, memo, useRef } from 'react';
import { Box } from '@mui/material';
import classNames from 'classnames';
import _isNil from 'lodash/isNil';
import StyledTooltip from '../../common/StyledTooltip';
import { NODE_WIDTH, NODE_HEIGHT } from '../const';
import {
  actionButtonDimensions,
  ActionButtonAdd,
  ActionButtonNurturingStep,
  ActionButtonConnectStep,
} from './components/CanvasButtons';
import { InnerNodeComponent, NodeComponent } from './components/SweepNodeComponent';
import { SweepNodeHandles } from './SweepNodeHandles';
import { DropNodeStateEnum } from '../useCalculateDragAndDrop';
import { colors, Typography } from '@sweep-io/sweep-design';
import { Sparkle } from '@sweep-io/sweep-design/dist/icons';
import { TruncatedTextTooltip } from '../../common/TruncatedTextTooltip';
import { NodeEntityTypes } from '../canvas-types';
import { Pills } from './components/pills/Pills';
import { RfNodeRegularNode } from '../canvas-types/nodeTypesData';
import { NodeProps } from '@xyflow/react';
import { useLongClick } from './useLongClick';
import { DragHandle } from './DragHandle';
const invalidReasonTooltipTextMap = {
  [DropNodeStateEnum.INVALID]: 'Positions cannot overlap',
  [DropNodeStateEnum.VALID_SWITCH]: 'Swap steps',
};

const MetadataChip = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '2px',
      display: 'flex',
      padding: '3px 8px',
    }}
  >
    <Typography variant="caption">{children}</Typography>
  </Box>
);

export const RegularStep = memo(
  ({
    id,
    selected,
    dragging,
    data: {
      name,
      objectType,
      onAddNodeClick,
      onNodeClick,
      onPillClick,
      onConnectClick,
      readonly,
      dropState,
      swapState,
      isHighlighted: _isHighlighted,
      hasSourceButton,
      hasTargetButton,
      showButtonsOnHighlight,
      probability,
      forecastCategoryLabel,
      parentId,
      pills,
      disableStepClick,
      hideNbButton,
      showSparkleIcon,
      isNb,
      nodeVisibility,
      simpleHighlighted,
    },
  }: NodeProps<RfNodeRegularNode>) => {
    const isHighlighted = !dragging && _isHighlighted;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const nodeHorizontalCenter = NODE_WIDTH / 2 - actionButtonDimensions.width / 2;
    const nodeVerticalCenter = NODE_HEIGHT / 2 - actionButtonDimensions.height / 2;

    const renderAddBranch = () => {
      return (
        <Box
          sx={{
            position: 'absolute',
            left: nodeHorizontalCenter,
            top: -45,
            cursor: 'default',
          }}
        >
          <ActionButtonAdd
            onClick={() =>
              onAddNodeClick({
                buttonPosition: 'top',
                sourceNodeId: id,
              })
            }
          />
        </Box>
      );
    };

    const renderAddSource = () => {
      return (
        <Box
          sx={{
            position: 'absolute',
            left: -45,
            top: nodeVerticalCenter,
            cursor: 'default',
          }}
        >
          <ActionButtonAdd
            onClick={() =>
              onAddNodeClick({
                buttonPosition: 'left',
                sourceNodeId: id,
              })
            }
          />
        </Box>
      );
    };

    const renderAddTarget = () => {
      return (
        <Box
          sx={{
            position: 'absolute',
            right: -45,
            top: nodeVerticalCenter,
            cursor: 'default',
          }}
        >
          <ActionButtonAdd
            onClick={() =>
              onAddNodeClick({
                buttonPosition: 'right',
                sourceNodeId: id,
              })
            }
          />
        </Box>
      );
    };

    const renderAddNurturingBucket = () => {
      if (hideNbButton) return null;
      return (
        <Box
          sx={{
            position: 'absolute',
            left: nodeHorizontalCenter,
            cursor: 'default',
            top: pills?.length ? NODE_HEIGHT + 35 : NODE_HEIGHT + 14,
          }}
        >
          <ActionButtonNurturingStep
            onClick={() =>
              onAddNodeClick({
                buttonPosition: 'bottom',
                sourceNodeId: id,
              })
            }
          />
        </Box>
      );
    };

    const renderConnectStepButton = () => (
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: -45,
          cursor: 'default',
        }}
      >
        <ActionButtonConnectStep
          onClick={
            onConnectClick
              ? (event) =>
                  onConnectClick(
                    event,
                    {
                      stepId: id,
                      groupId: parentId,
                    },
                    wrapperRef.current,
                  )
              : undefined
          }
        />
      </Box>
    );

    const renderSparkleIcon = () => (
      <Box
        sx={{
          backgroundColor: colors.blue[500],
          height: '20px',
          width: '20px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          zIndex: 1,
          right: '-5px',
          top: '-5px',
        }}
      >
        <Sparkle variant="tiny" color={colors.white} />
      </Box>
    );

    const isInvalidPosition = dropState === DropNodeStateEnum.INVALID;
    const tooltipText =
      dropState && dropState !== DropNodeStateEnum.VALID && invalidReasonTooltipTextMap[dropState];

    const showButtons = isHighlighted && showButtonsOnHighlight;
    const showProbability = !_isNil(probability);

    const nodeVisibilityIsOnlyStep = nodeVisibility === 'onlyStep';
    const nodeVisibilityIsDoNotShowMetadata = nodeVisibility === 'doNotShowMetadata';
    const nodeVisibilityIsShowAll = nodeVisibility === 'showAll';

    const showPills = nodeVisibilityIsShowAll && pills;
    const showInnerContent = nodeVisibilityIsShowAll || nodeVisibilityIsDoNotShowMetadata;
    const showMetadata = nodeVisibilityIsShowAll && (showProbability || forecastCategoryLabel);

    const onClickBuilder = (isLongClick: boolean) => {
      return (event: React.MouseEvent | React.TouchEvent) => {
        onNodeClick?.({
          id,
          parentId,
          event,
          entity: {
            type: NodeEntityTypes.Step,
          },
          isLongClick,
        });
      };
    };

    const { longClickProps } = useLongClick({
      onLongClick: onClickBuilder(true),
      onShortClick: onClickBuilder(false),
    });

    return (
      <SweepNodeHandles>
        {showSparkleIcon && !nodeVisibilityIsOnlyStep && renderSparkleIcon()}
        {showButtons && (
          <>
            {hasSourceButton && renderAddSource()}
            {hasTargetButton && renderAddTarget()}
            {!isNb && (
              <>
                {renderAddBranch()}
                {renderAddNurturingBucket()}
              </>
            )}
            {renderConnectStepButton()}
          </>
        )}
        <StyledTooltip
          title={tooltipText}
          open={Boolean(tooltipText)}
          placement="top"
          sx={{
            maxWidth: 'inherit',
            zIndex: '5 !important', //force overwrite to avoid errors appearing over dialogs
          }}
        >
          <NodeComponent
            data-testid="sweep-canvas-regular-node"
            className={classNames({
              highlighted:
                !nodeVisibilityIsOnlyStep && (isHighlighted || selected || simpleHighlighted),
              'invalid-position': isInvalidPosition,
              'nb-node': isNb,
              shadow: !nodeVisibilityIsOnlyStep && !swapState,
              [`node-${objectType}`]: true,
              'has-opacity': nodeVisibilityIsOnlyStep,
              'disable-step-click': disableStepClick,
              'swap-step-target': swapState === 'target',
              'swap-step-source': swapState === 'source',
            })}
            ref={wrapperRef}
          >
            <InnerNodeComponent {...longClickProps}>
              {showInnerContent && (
                <>
                  <TruncatedTextTooltip
                    variant={nodeVisibilityIsDoNotShowMetadata ? 'h4' : 'h4'}
                    disablePortal
                  >
                    {name}
                  </TruncatedTextTooltip>
                  {showMetadata && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        gap: '4px',
                      }}
                      className="metadata-data"
                    >
                      {showProbability && <MetadataChip>{probability + ''}</MetadataChip>}
                      {forecastCategoryLabel && (
                        <MetadataChip>{forecastCategoryLabel}</MetadataChip>
                      )}
                    </Box>
                  )}
                </>
              )}

              {(dragging || isHighlighted) && !readonly && !nodeVisibilityIsOnlyStep && (
                <Box
                  className="drag-handle"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 40,
                    height: '100%',
                    cursor: 'move',
                    '&:active': {
                      cursor: 'move',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DragHandle isDragging={dragging} />
                </Box>
              )}
            </InnerNodeComponent>
            {showPills && (
              <Box
                sx={{
                  marginTop: '12px', // 10px padding + 2px of shadow
                }}
              >
                <Pills
                  nodeId={id}
                  nodeLabel={name}
                  parentNodeId={parentId}
                  objectType={objectType}
                  onPillClick={onPillClick}
                  pills={pills}
                  entityType={NodeEntityTypes.StepPill}
                  pillsMax={4}
                />
              </Box>
            )}
          </NodeComponent>
        </StyledTooltip>
      </SweepNodeHandles>
    );
  },
);
